import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet, Link } from 'react-router-dom';
import { getUser } from '../services/auth';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import SideNavLayout from '../components/SideNavLayout';
import ContentHeader from '../components/ContentHeader';
import Avatar from '../components/Avatar';
import './SuperAdmin.css';

function getSuperAdminPath(segment, tenantPath) {
  const rel = `/super-admin/${segment}`;
  return tenantPath ? tenantPath(rel) : rel;
}

function SuperAdmin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantPath } = useTenantFromUrl();
  const pathname = location.pathname;

  // Fix URL when first segment is "super-admin" (mistaken as tenant) → /:sessionTenant/super-admin/...
  useEffect(() => {
    const path = location.pathname || '';
    const sessionTenant = getUser()?.tenant?.code;
    if (sessionTenant && path.match(/^\/super-admin(\/|$)/)) {
      const rest = path.replace(/^\/super-admin\/?/, '') || 'tenants';
      const correct = `/${sessionTenant}/super-admin/${rest}`;
      if (path !== correct) {
        navigate(correct, { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  const navItems = [
    { id: 'tenants', name: 'Tenants' },
    { id: 'orgs', name: 'Organizations' },
    { id: 'users', name: 'Users' },
    { id: 'memberships', name: 'Tenant memberships' },
    { id: 'support-cases', name: 'Support cases' },
    { id: 'releases', name: 'Releases' },
    { id: 'billing', name: 'Billing' },
    { id: 'jobs', name: 'Scheduled jobs' },
  ];

  const [hintExpanded, setHintExpanded] = useState(false);
  const isActive = (id) => {
    const base = pathname.replace(/\/$/, '');
    const target = getSuperAdminPath(id, tenantPath).replace(/\/$/, '');
    return base === target || base.startsWith(target + '/');
  };

  const nav = (
    <>
      <div className="super-admin-nav-header">
        <button
          type="button"
          className="super-admin-nav-title-btn"
          onClick={() => setHintExpanded((prev) => !prev)}
          aria-expanded={hintExpanded}
          aria-label={hintExpanded ? 'Hide description' : 'Show description'}
        >
          Super Admin
          <span className="super-admin-nav-hint-icon" aria-hidden>ⓘ</span>
        </button>
        {hintExpanded && (
          <p className="super-admin-nav-hint">System-level management (DEVELOPER access only)</p>
        )}
      </div>
      <div className="super-admin-nav-body">
        {navItems.map((item) => (
          <Link
            key={item.id}
            to={getSuperAdminPath(item.id, tenantPath)}
            className={`super-admin-nav-item ${isActive(item.id) ? 'active' : ''}`}
          >
            {item.name}
          </Link>
        ))}
      </div>
    </>
  );

  return (
    <div className="super-admin">
      <SideNavLayout nav={nav}>
        <Outlet />
      </SideNavLayout>
    </div>
  );
}

export function SuperAdminTenantsTab() {
  const [tenants, setTenants] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    org_id: '',
    code: '',
    name: '',
    is_active: true,
    at_packet_enabled: false,
    checklist_enabled: false,
    file_review_enabled: false,
    client_services_provider_search_enabled: false,
    client_services_central_availability_enabled: false,
    training_enabled: false,
    data_corrections_enabled: false,
    monthly_contact_enabled: false,
    travel_voucher_enabled: false,
    travel_voucher_rate_cents_per_mile: '',
    client_work_tasks_enabled: true,
    support_cases_enabled: false,
    support_default_agent_user_id: '',
  });

  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const { superAdminAPI, supportAgentAPI } = await import('../services/api');
      const [tenantsRes, orgsRes, agentsRes] = await Promise.all([
        superAdminAPI.getAllTenants(),
        superAdminAPI.getAllOrgs(),
        supportAgentAPI.listAgents().catch(() => ({ data: { data: [] } })),
      ]);
      setTenants(tenantsRes.data.data || []);
      setOrgs(orgsRes.data.data || []);
      setAgents(agentsRes.data?.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingId(null);
    setFormData({ org_id: '', code: '', name: '', is_active: true, at_packet_enabled: false, checklist_enabled: false, file_review_enabled: false, client_services_provider_search_enabled: false, client_services_central_availability_enabled: false, training_enabled: false, data_corrections_enabled: false, monthly_contact_enabled: false, travel_voucher_enabled: false, travel_voucher_rate_cents_per_mile: '', client_work_tasks_enabled: true, support_cases_enabled: false, support_default_agent_user_id: '' });
  };

  const handleEdit = (tenant) => {
    setEditingId(tenant.id);
    setIsCreating(false);
    setFormData({
      org_id: tenant.org_id,
      code: tenant.code,
      name: tenant.name || '',
      is_active: tenant.is_active,
      at_packet_enabled: tenant.at_packet_enabled === true,
      checklist_enabled: tenant.checklist_enabled === true,
      file_review_enabled: tenant.file_review_enabled === true,
      client_services_provider_search_enabled: tenant.client_services_provider_search_enabled === true,
      client_services_central_availability_enabled: tenant.client_services_central_availability_enabled === true,
      training_enabled: tenant.training_enabled === true,
      data_corrections_enabled: tenant.data_corrections_enabled === true,
      monthly_contact_enabled: tenant.monthly_contact_enabled === true,
      travel_voucher_enabled: tenant.travel_voucher_enabled === true,
      travel_voucher_rate_cents_per_mile: tenant.travel_voucher_rate_cents_per_mile != null ? String(tenant.travel_voucher_rate_cents_per_mile) : '',
      client_work_tasks_enabled: tenant.client_work_tasks_enabled !== false,
      support_cases_enabled: tenant.support_cases_enabled === true,
      support_default_agent_user_id: tenant.support_default_agent_user_id != null ? String(tenant.support_default_agent_user_id) : '',
    });
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData({ org_id: '', code: '', name: '', is_active: true, at_packet_enabled: false, checklist_enabled: false, file_review_enabled: false, client_services_provider_search_enabled: false, client_services_central_availability_enabled: false, training_enabled: false, data_corrections_enabled: false, monthly_contact_enabled: false, travel_voucher_enabled: false, travel_voucher_rate_cents_per_mile: '', client_work_tasks_enabled: true, support_cases_enabled: false, support_default_agent_user_id: '' });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    try {
      setError(null);
      const { superAdminAPI } = await import('../services/api');
      
      if (isCreating) {
        await superAdminAPI.createTenant({
          org_id: formData.org_id,
          code: formData.code,
          name: formData.name,
          is_active: formData.is_active,
        });
      } else {
        await superAdminAPI.updateTenant(editingId, {
          org_id: formData.org_id,
          code: formData.code,
          name: formData.name,
          is_active: formData.is_active,
        });
        await superAdminAPI.updateTenantSettings(editingId, {
          at_packet_enabled: !!formData.at_packet_enabled,
          checklist_enabled: !!formData.checklist_enabled,
          file_review_enabled: !!formData.file_review_enabled,
          client_services_provider_search_enabled: !!formData.client_services_provider_search_enabled,
          client_services_central_availability_enabled: !!formData.client_services_central_availability_enabled,
          training_enabled: !!formData.training_enabled,
          data_corrections_enabled: !!formData.data_corrections_enabled,
          monthly_contact_enabled: !!formData.monthly_contact_enabled,
          travel_voucher_enabled: !!formData.travel_voucher_enabled,
          travel_voucher_rate_cents_per_mile: (() => {
            const v = formData.travel_voucher_rate_cents_per_mile;
            if (v === '') return null;
            const n = parseFloat(v);
            return Number.isFinite(n) ? n : null;
          })(),
          client_work_tasks_enabled: !!formData.client_work_tasks_enabled,
          support_cases_enabled: !!formData.support_cases_enabled,
          support_default_agent_user_id: formData.support_default_agent_user_id === '' ? null : formData.support_default_agent_user_id,
        });
      }

      await loadData();
      handleCancel();
    } catch (err) {
      console.error('Error saving tenant:', err);
      setError(err.response?.data?.error || 'Failed to save tenant');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this tenant?')) {
      return;
    }
    
    try {
      setError(null);
      const { superAdminAPI } = await import('../services/api');
      await superAdminAPI.deleteTenant(id);
      await loadData();
    } catch (err) {
      console.error('Error deleting tenant:', err);
      setError(err.response?.data?.error || 'Failed to delete tenant');
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="super-admin-tab-content">
      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      <ContentHeader
        title="Tenants"
        actions={
          <button className="btn btn-primary" onClick={handleCreate}>
            + Create Tenant
          </button>
        }
      />

      {(isCreating || editingId) && (
        <div className="super-admin-form">
          <h2>{isCreating ? 'Create Tenant' : 'Edit Tenant'}</h2>
          <div className="form-group">
            <label>Organization *</label>
            <select
              name="org_id"
              value={formData.org_id}
              onChange={handleChange}
              required
              disabled={!!editingId}
            >
              <option value="">Select organization...</option>
              {orgs.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Tenant Code *</label>
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              required
              disabled={!!editingId}
            />
          </div>
          <div className="form-group">
            <label>Tenant Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
          </div>
          <div className="form-group form-group-tenant-active">
            <label className="tenant-active-row">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />
              <span className="tenant-active-label">Tenant active</span>
            </label>
          </div>
          {!isCreating && (
            <div className="form-group super-admin-tenant-features">
              <div className="features-box">
                <div className="features-box-title">Features</div>
                <label className="feature-row">
                  <input
                    type="checkbox"
                    name="at_packet_enabled"
                    checked={!!formData.at_packet_enabled}
                    onChange={handleChange}
                  />
                  <span>AT Packets (Assistive Technology request packets)</span>
                </label>
                <label className="feature-row">
                  <input
                    type="checkbox"
                    name="checklist_enabled"
                    checked={!!formData.checklist_enabled}
                    onChange={handleChange}
                  />
                  <span>Checklist (referral-scoped checklist groups and items)</span>
                </label>
                <label className="feature-row">
                  <input
                    type="checkbox"
                    name="file_review_enabled"
                    checked={!!formData.file_review_enabled}
                    onChange={handleChange}
                  />
                  <span>File Review (file review template and reviews)</span>
                </label>
                <label className="feature-row">
                  <input
                    type="checkbox"
                    name="client_services_provider_search_enabled"
                    checked={!!formData.client_services_provider_search_enabled}
                    onChange={handleChange}
                  />
                  <span>Client Services – Provider Search (needs list, search providers, email BCC, export CSV)</span>
                </label>
                <label className="feature-row">
                  <input
                    type="checkbox"
                    name="client_services_central_availability_enabled"
                    checked={!!formData.client_services_central_availability_enabled}
                    onChange={handleChange}
                  />
                  <span>Client Services – Central Availability (future)</span>
                </label>
                <label className="feature-row">
                  <input
                    type="checkbox"
                    name="training_enabled"
                    checked={!!formData.training_enabled}
                    onChange={handleChange}
                  />
                  <span>Training (training windows, activities, credentials, activity and monitor reports)</span>
                </label>
                <label className="feature-row">
                  <input
                    type="checkbox"
                    name="data_corrections_enabled"
                    checked={!!formData.data_corrections_enabled}
                    onChange={handleChange}
                  />
                  <span>Data Corrections (manager-only list and form; Main/Supplemental doc-gen)</span>
                </label>
                <label className="feature-row">
                  <input
                    type="checkbox"
                    name="monthly_contact_enabled"
                    checked={!!formData.monthly_contact_enabled}
                    onChange={handleChange}
                  />
                  <span>Monthly Contact (left nav and Lists; SCs contact clients monthly, send batch emails)</span>
                </label>
                <label className="feature-row">
                  <input
                    type="checkbox"
                    name="travel_voucher_enabled"
                    checked={!!formData.travel_voucher_enabled}
                    onChange={handleChange}
                  />
                  <span>Travel Voucher Helper (Reports → Tools; export travel voucher data for date range, starting/ending addresses)</span>
                </label>
                <label className="feature-row">
                  <input
                    type="checkbox"
                    name="client_work_tasks_enabled"
                    checked={!!formData.client_work_tasks_enabled}
                    onChange={handleChange}
                  />
                  <span>Work Tasks (client-scoped work tasks on client left nav; track work type, task type, requested/received dates)</span>
                </label>
                <label className="feature-row">
                  <input
                    type="checkbox"
                    name="support_cases_enabled"
                    checked={!!formData.support_cases_enabled}
                    onChange={handleChange}
                  />
                  <span>Support Cases (users open bug/question/feature cases from Help &amp; Support; case-workers manage them)</span>
                </label>
                {formData.support_cases_enabled && (
                  <div className="feature-row feature-row--rate">
                    <label htmlFor="support_default_agent_user_id">Default case assignee</label>
                    <select
                      id="support_default_agent_user_id"
                      name="support_default_agent_user_id"
                      value={formData.support_default_agent_user_id}
                      onChange={handleChange}
                    >
                      <option value="">— (none: cases open unassigned)</option>
                      {agents.map((a) => (
                        <option key={a.id} value={a.id}>{a.name || a.username}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="feature-row feature-row--rate">
                  <label htmlFor="travel_voucher_rate_cents_per_mile">Travel voucher rate (¢/mile)</label>
                  <input
                    id="travel_voucher_rate_cents_per_mile"
                    type="number"
                    name="travel_voucher_rate_cents_per_mile"
                    min="0"
                    step="0.1"
                    value={formData.travel_voucher_rate_cents_per_mile}
                    onChange={handleChange}
                    placeholder="e.g. 72.5"
                  />
                </div>
                <p className="super-admin-field-hint">Optional. Used for the amount column when miles are provided (round-trip).</p>
              </div>
            </div>
          )}
          <div className="form-actions">
            <button className="btn btn-primary" onClick={handleSave}>
              Save
            </button>
            <button className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="super-admin-table">
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Organization</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-data">No tenants found</td>
              </tr>
            ) : (
              tenants.map((tenant) => (
                <tr
                  key={tenant.id}
                  className="super-admin-row-clickable"
                  onClick={() => handleEdit(tenant)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEdit(tenant); } }}
                >
                  <td>{tenant.code}</td>
                  <td>{tenant.name || '-'}</td>
                  <td>{tenant.org_name}</td>
                  <td>
                    <span className="super-admin-status-text">{tenant.is_active ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="btn btn-sm btn-deactivate"
                      onClick={() => handleDelete(tenant.id)}
                      title="Deactivate tenant (disable access; does not delete data)"
                    >
                      Deactivate
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SuperAdminOrgsTab() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    is_active: true,
  });

  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const { superAdminAPI } = await import('../services/api');
      const res = await superAdminAPI.getAllOrgs();
      setOrgs(res.data.data || []);
    } catch (err) {
      console.error('Error loading organizations:', err);
      setError(err.response?.data?.error || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingId(null);
    setFormData({ name: '', is_active: true });
  };

  const handleEdit = (org) => {
    setEditingId(org.id);
    setIsCreating(false);
    setFormData({
      name: org.name,
      is_active: org.is_active,
    });
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData({ name: '', is_active: true });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    try {
      setError(null);
      const { superAdminAPI } = await import('../services/api');
      
      if (isCreating) {
        await superAdminAPI.createOrg(formData);
      } else {
        await superAdminAPI.updateOrg(editingId, formData);
      }
      
      await loadData();
      handleCancel();
    } catch (err) {
      console.error('Error saving organization:', err);
      setError(err.response?.data?.error || 'Failed to save organization');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this organization?')) {
      return;
    }
    
    try {
      setError(null);
      const { superAdminAPI } = await import('../services/api');
      await superAdminAPI.deleteOrg(id);
      await loadData();
    } catch (err) {
      console.error('Error deleting organization:', err);
      setError(err.response?.data?.error || 'Failed to delete organization');
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="super-admin-tab-content">
      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      <ContentHeader
        title="Organizations"
        actions={
          <button className="btn btn-primary" onClick={handleCreate}>
            + Create Organization
          </button>
        }
      />

      {(isCreating || editingId) && (
        <div className="super-admin-form">
          <h2>{isCreating ? 'Create Organization' : 'Edit Organization'}</h2>
          <div className="form-group">
            <label>Organization Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />
              Active
            </label>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" onClick={handleSave}>
              Save
            </button>
            <button className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="super-admin-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Tenants</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orgs.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-data">No organizations found</td>
              </tr>
            ) : (
              orgs.map((org) => (
                <tr
                  key={org.id}
                  className="super-admin-row-clickable"
                  onClick={() => handleEdit(org)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEdit(org); } }}
                >
                  <td>{org.name}</td>
                  <td>{org.tenant_count || 0}</td>
                  <td>
                    <span className="super-admin-status-text">{org.is_active ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="btn btn-sm btn-deactivate"
                      onClick={() => handleDelete(org.id)}
                      title="Deactivate organization (disable; does not delete data)"
                    >
                      Deactivate
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SuperAdminMembershipsTab() {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addForm, setAddForm] = useState({ user_id: '', tenant_id: '', role_id: '', is_enabled: true });
  const [rolesForTenant, setRolesForTenant] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const { superAdminAPI } = await import('../services/api');
      const [usersRes, tenantsRes] = await Promise.all([
        superAdminAPI.getAllUsers().catch(() => ({ data: { data: [] } })),
        superAdminAPI.getAllTenants().catch(() => ({ data: { data: [] } })),
      ]);
      setUsers(usersRes.data.data || []);
      setTenants(tenantsRes.data.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!addForm.tenant_id) {
      setRolesForTenant([]);
      setAddForm((prev) => ({ ...prev, role_id: '' }));
      return;
    }
    setAddForm((prev) => ({ ...prev, role_id: '' }));
    let cancelled = false;
    import('../services/api').then(({ superAdminAPI }) =>
      superAdminAPI.getAllRoles(parseInt(addForm.tenant_id, 10))
    ).then((res) => {
      if (!cancelled) setRolesForTenant(res?.data?.data || []);
    }).catch(() => {
      if (!cancelled) setRolesForTenant([]);
    });
    return () => { cancelled = true; };
  }, [addForm.tenant_id]);

  React.useEffect(() => {
    loadData();
  }, []);

  // Flatten: one row per (user, tenant, role)
  const rows = [];
  users.forEach((user) => {
    const memberships = user.tenant_memberships || [];
    if (memberships.length === 0) {
      rows.push({ user_id: user.id, username: user.username || user.email || '-', tenant_code: null, tenant_id: null, role_id: null, role_code: null, role_name: null, is_enabled: null });
    } else {
      memberships.forEach((m) => {
        rows.push({
          user_id: user.id,
          username: user.username || user.email || '-',
          tenant_code: m.tenant_code,
          tenant_id: m.tenant_id,
          tenant_name: m.tenant_name,
          role_id: m.role_id,
          role_code: m.role_code,
          role_name: m.role_name,
          is_enabled: m.is_enabled,
        });
      });
    }
  });

  const handleAddFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddMembership = async () => {
    if (!addForm.user_id || !addForm.tenant_id || !addForm.role_id) {
      setError('Please select user, tenant, and role');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const { superAdminAPI } = await import('../services/api');
      await superAdminAPI.addUserMembership(parseInt(addForm.user_id, 10), {
        tenant_id: parseInt(addForm.tenant_id, 10),
        role_id: parseInt(addForm.role_id, 10),
        is_enabled: !!addForm.is_enabled,
      });
      await loadData();
      setAddForm((prev) => ({ ...prev, role_id: '', is_enabled: true }));
    } catch (err) {
      console.error('Error saving membership:', err);
      setError(err.response?.data?.error || 'Failed to save membership');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRole = async (userId, tenantId, roleId) => {
    if (!window.confirm('Remove this role from the user in this tenant?')) return;
    try {
      setSaving(true);
      setError(null);
      const { superAdminAPI } = await import('../services/api');
      await superAdminAPI.removeUserMembership(userId, tenantId, roleId);
      await loadData();
    } catch (err) {
      console.error('Error removing role:', err);
      setError(err.response?.data?.error || 'Failed to remove role');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async (userId, tenantId) => {
    if (!window.confirm('Disable this tenant membership? The user will no longer have access to that tenant.')) return;
    try {
      setSaving(true);
      setError(null);
      const { superAdminAPI } = await import('../services/api');
      await superAdminAPI.disableUserMembership(userId, tenantId);
      await loadData();
    } catch (err) {
      console.error('Error disabling membership:', err);
      setError(err.response?.data?.error || 'Failed to disable membership');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="super-admin-tab-content">
      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}
      <ContentHeader
        title="Tenant memberships"
        titleHint="Assign users to tenants and roles. Users need at least one membership to log in and use the app."
      />
      <div className="super-admin-actions super-admin-actions--full" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            name="user_id"
            value={addForm.user_id}
            onChange={handleAddFormChange}
            disabled={saving}
          >
            <option value="">Select user…</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.username || u.email || '-'}
              </option>
            ))}
          </select>
          <select
            name="tenant_id"
            value={addForm.tenant_id}
            onChange={handleAddFormChange}
            disabled={saving}
          >
            <option value="">Select tenant…</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.code}{t.name ? ` - ${t.name}` : ''}
              </option>
            ))}
          </select>
          <select
            name="role_id"
            value={addForm.role_id}
            onChange={handleAddFormChange}
            disabled={saving || !addForm.tenant_id}
          >
            <option value="">Select role…</option>
            {rolesForTenant.map((r) => (
              <option key={r.id} value={r.id}>
                {r.code} - {r.name}
              </option>
            ))}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input
              type="checkbox"
              name="is_enabled"
              checked={!!addForm.is_enabled}
              onChange={handleAddFormChange}
              disabled={saving}
            />
            Enabled
          </label>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAddMembership}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Add membership'}
          </button>
        </div>
      </div>
      <div className="super-admin-table">
        <table>
          <thead>
            <tr>
              <th>User</th>
              <th>Tenant</th>
              <th>Role</th>
              <th>Enabled</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-data">No memberships. Add one above.</td>
              </tr>
            ) : (
              rows
                .filter((r) => r.tenant_id != null)
                .map((r) => (
                  <tr key={`${r.user_id}-${r.tenant_id}-${r.role_id}`}>
                    <td>{r.username}</td>
                    <td>{r.tenant_code}{r.tenant_name ? ` - ${r.tenant_name}` : ''}</td>
                    <td>{r.role_code} - {r.role_name}</td>
                    <td>{r.is_enabled ? 'Yes' : 'No'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleRemoveRole(r.user_id, r.tenant_id, r.role_id)}
                        disabled={saving}
                      >
                        Remove role
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDisable(r.user_id, r.tenant_id)}
                        disabled={saving || !r.is_enabled}
                        title="Disable all roles for this user in this tenant"
                      >
                        Disable tenant
                      </button>
                    </td>
                  </tr>
                ))
            )}
          </tbody>
        </table>
      </div>
      <p style={{ marginTop: '12px', fontSize: '0.9rem', color: '#6c757d' }}>
        Users with no memberships are not listed above. Use &quot;Add membership&quot; or edit a user in the Users tab to assign them to a tenant.
      </p>
    </div>
  );
}

export function SuperAdminUsersTab() {
  const [users, setUsers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [memberships, setMemberships] = useState([]);
  const [membershipForm, setMembershipForm] = useState({ tenant_id: '', role_id: '', is_enabled: true });
  const [membershipSaving, setMembershipSaving] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState(0);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    staff_id: '',
    is_enabled: true,
  });

  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const { superAdminAPI } = await import('../services/api');
      const [usersRes, staffRes, tenantsRes] = await Promise.all([
        superAdminAPI.getAllUsers().catch(() => ({ data: { data: [] } })),
        superAdminAPI.getAllStaff().catch(() => ({ data: { data: [] } })),
        superAdminAPI.getAllTenants().catch(() => ({ data: { data: [] } })),
      ]);
      setUsers(usersRes.data.data || []);
      setStaff(staffRes.data.data || []);
      setTenants(tenantsRes.data.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingId(null);
    setFormData({ username: '', email: '', staff_id: '', is_enabled: true });
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setIsCreating(false);
    setFormData({
      username: user.username || '',
      email: user.email || '',
      staff_id: user.staff_id ? String(user.staff_id) : '',
      is_enabled: user.is_enabled,
    });
    setMemberships(user.tenant_memberships || []);
    setMembershipForm({ tenant_id: '', role_id: '', is_enabled: true });
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData({ username: '', email: '', staff_id: '', is_enabled: true });
    setMemberships([]);
    setMembershipForm({ tenant_id: '', role_id: '', is_enabled: true });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    try {
      setError(null);
      const { superAdminAPI } = await import('../services/api');
      
      // Convert staff_id to number or null
      const dataToSave = {
        ...formData,
        staff_id: formData.staff_id ? parseInt(formData.staff_id, 10) : null,
      };
      
      if (isCreating) {
        await superAdminAPI.createUser(dataToSave);
      } else {
        await superAdminAPI.updateUser(editingId, dataToSave);
      }
      
      await loadData();
      handleCancel();
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to disable this user?')) {
      return;
    }
    
    try {
      setError(null);
      const { superAdminAPI } = await import('../services/api');
      await superAdminAPI.deleteUser(id);
      await loadData();
    } catch (err) {
      console.error('Error disabling user:', err);
      setError(err.response?.data?.error || 'Failed to disable user');
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingId) return;
    try {
      const { superAdminAPI } = await import('../services/api');
      await superAdminAPI.uploadUserAvatar(editingId, file);
      setAvatarVersion((v) => v + 1);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload avatar');
    } finally {
      e.target.value = '';
    }
  };

  const handleAvatarRemove = async () => {
    if (!editingId || !window.confirm("Remove this user's photo?")) return;
    try {
      const { superAdminAPI } = await import('../services/api');
      await superAdminAPI.deleteUserAvatar(editingId);
      setAvatarVersion((v) => v + 1);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove avatar');
    }
  };

  const refreshMemberships = async () => {
    if (!editingId) return;
    try {
      const { superAdminAPI } = await import('../services/api');
      const res = await superAdminAPI.getUserMemberships(editingId);
      setMemberships(res.data.data || []);
    } catch (err) {
      console.error('Error loading memberships:', err);
      setError(err.response?.data?.error || 'Failed to load memberships');
    }
  };

  const handleMembershipFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMembershipForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const [rolesForSelectedTenant, setRolesForSelectedTenant] = useState([]);
  React.useEffect(() => {
    if (!membershipForm.tenant_id) {
      setRolesForSelectedTenant([]);
      setMembershipForm((prev) => ({ ...prev, role_id: '' }));
      return;
    }
    setMembershipForm((prev) => ({ ...prev, role_id: '' }));
    let cancelled = false;
    import('../services/api').then(({ superAdminAPI }) =>
      superAdminAPI.getAllRoles(parseInt(membershipForm.tenant_id, 10))
    ).then((res) => {
      if (!cancelled) setRolesForSelectedTenant(res?.data?.data || []);
    }).catch(() => {
      if (!cancelled) setRolesForSelectedTenant([]);
    });
    return () => { cancelled = true; };
  }, [membershipForm.tenant_id]);

  const handleAddOrUpdateMembership = async () => {
    if (!editingId) return;
    if (!membershipForm.tenant_id || !membershipForm.role_id) {
      setError('Please select both a tenant and a role');
      return;
    }
    try {
      setMembershipSaving(true);
      setError(null);
      const { superAdminAPI } = await import('../services/api');
      const addRes = await superAdminAPI.addUserMembership(editingId, {
        tenant_id: parseInt(membershipForm.tenant_id, 10),
        role_id: parseInt(membershipForm.role_id, 10),
        is_enabled: !!membershipForm.is_enabled,
      });
      // Optimistic: show new row immediately from POST response
      const newRow = addRes?.data?.data;
      if (newRow && typeof newRow === 'object') {
        setMemberships((prev) => [...prev, newRow]);
      }
      setMembershipForm((prev) => ({ ...prev, role_id: '', is_enabled: true }));
      // Then sync full list from server
      await refreshMemberships();
      await loadData();
    } catch (err) {
      console.error('Error saving membership:', err);
      setError(err.response?.data?.error || 'Failed to save membership');
    } finally {
      setMembershipSaving(false);
    }
  };

  const handleRemoveMembershipRole = async (tenantId, roleId) => {
    if (!editingId) return;
    if (!window.confirm('Remove this role for the user in this tenant?')) return;
    try {
      setMembershipSaving(true);
      setError(null);
      const { superAdminAPI } = await import('../services/api');
      await superAdminAPI.removeUserMembership(editingId, tenantId, roleId);
      await refreshMemberships();
      await loadData();
    } catch (err) {
      console.error('Error removing role:', err);
      setError(err.response?.data?.error || 'Failed to remove role');
    } finally {
      setMembershipSaving(false);
    }
  };

  const handleDisableMembership = async (tenantId) => {
    if (!editingId) return;
    if (!window.confirm('Disable all roles for this user in this tenant?')) return;
    try {
      setMembershipSaving(true);
      setError(null);
      const { superAdminAPI } = await import('../services/api');
      await superAdminAPI.disableUserMembership(editingId, tenantId);
      await refreshMemberships();
      await loadData();
    } catch (err) {
      console.error('Error disabling membership:', err);
      setError(err.response?.data?.error || 'Failed to disable membership');
    } finally {
      setMembershipSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="super-admin-tab-content">
      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      <ContentHeader
        title="Users"
        actions={
          <button className="btn btn-primary" onClick={handleCreate}>
            + Create User
          </button>
        }
      />

      {(isCreating || editingId) && (
        <div className="super-admin-form">
          <h2>{isCreating ? 'Create User' : 'Edit User'}</h2>
          {editingId && (
            <div className="form-group super-admin-avatar-row">
              <label>Avatar</label>
              <div className="super-admin-avatar" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Avatar userId={editingId} name={formData.username || formData.email} version={avatarVersion} size={64} />
                <label className="btn btn-secondary">
                  Change photo
                  <input type="file" accept="image/*" hidden onChange={handleAvatarUpload} />
                </label>
                <button type="button" className="btn btn-secondary" onClick={handleAvatarRemove}>Remove</button>
              </div>
            </div>
          )}
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Optional if email provided"
            />
            <small>Username or email is required</small>
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Optional if username provided"
            />
          </div>
          <div className="form-group">
            <label>Staff Member (optional)</label>
            <select
              name="staff_id"
              value={formData.staff_id}
              onChange={handleChange}
            >
              <option value="">-- No staff link --</option>
              {staff.map((s) => {
                const displayName = `${s.first_name} ${s.last_name} (${s.tenant_code})`;
                return (
                  <option key={s.id} value={s.id}>
                    {displayName}
                  </option>
                );
              })}
            </select>
            <small>Link this user to a staff member (optional)</small>
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                name="is_enabled"
                checked={formData.is_enabled}
                onChange={handleChange}
              />
              Enabled
            </label>
            <small>Disabled users cannot log in</small>
          </div>
          {isCreating && (
            <div className="alert alert-info">
              <strong>Note:</strong> User will need to set their password via "Set Initial Password" on the login page.
            </div>
          )}
          <div className="form-actions">
            <button className="btn btn-primary" onClick={handleSave}>
              Save
            </button>
            <button className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>

          {editingId && (
            <div style={{ marginTop: '20px' }}>
              <h3 style={{ marginBottom: '10px' }}>Tenant Memberships</h3>
              <div className="form-group">
                <label>Add / Update Membership</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    name="tenant_id"
                    value={membershipForm.tenant_id}
                    onChange={handleMembershipFormChange}
                    disabled={membershipSaving}
                  >
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.code}{t.name ? ` - ${t.name}` : ''}
                      </option>
                    ))}
                  </select>
                  <select
                    name="role_id"
                    value={membershipForm.role_id}
                    onChange={handleMembershipFormChange}
                    disabled={membershipSaving || !membershipForm.tenant_id}
                  >
                    <option value="">Select role…</option>
                    {rolesForSelectedTenant.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.code} - {r.name}
                      </option>
                    ))}
                  </select>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="checkbox"
                      name="is_enabled"
                      checked={!!membershipForm.is_enabled}
                      onChange={handleMembershipFormChange}
                      disabled={membershipSaving}
                    />
                    Enabled
                  </label>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAddOrUpdateMembership}
                    disabled={membershipSaving}
                  >
                    {membershipSaving ? 'Saving…' : 'Save Membership'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={refreshMemberships}
                    disabled={membershipSaving}
                  >
                    Refresh
                  </button>
                </div>
                <small>Assign this user to a tenant and role (required for login).</small>
              </div>

              <div className="super-admin-table" style={{ marginTop: '10px' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Role</th>
                      <th>Enabled</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberships.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="no-data">No memberships</td>
                      </tr>
                    ) : (
                      memberships.map((m) => (
                        <tr key={`${m.tenant_id}-${m.role_id}`}>
                          <td>{m.tenant_code}{m.tenant_name ? ` - ${m.tenant_name}` : ''}</td>
                          <td>{m.role_code} - {m.role_name}</td>
                          <td>{m.is_enabled ? 'Yes' : 'No'}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              onClick={() => handleRemoveMembershipRole(m.tenant_id, m.role_id)}
                              disabled={membershipSaving}
                            >
                              Remove role
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDisableMembership(m.tenant_id)}
                              disabled={membershipSaving || !m.is_enabled}
                              title="Disable all roles for this user in this tenant"
                            >
                              Disable tenant
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="super-admin-table">
        <table>
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Staff</th>
              <th>Tenant Memberships</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">No users found</td>
              </tr>
            ) : (
              users.map((user) => {
                const staffName = user.staff_first_name && user.staff_last_name
                  ? `${user.staff_first_name} ${user.staff_last_name}`
                  : user.staff_id ? `Staff #${user.staff_id}` : '-';
                const memberships = user.tenant_memberships || [];
                const membershipText = memberships.length > 0
                  ? memberships.map(m => `${m.tenant_code} (${m.role_code})`).join(', ')
                  : 'None';
                return (
                  <tr
                    key={user.id}
                    className="super-admin-row-clickable"
                    onClick={() => handleEdit(user)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEdit(user); } }}
                  >
                    <td>{user.username || '-'}</td>
                    <td>{user.email || '-'}</td>
                    <td>{staffName}</td>
                    <td>
                      <span title={membershipText}>
                        {memberships.length > 0 ? `${memberships.length} tenant(s)` : 'None'}
                      </span>
                    </td>
                    <td>
                      <span className="super-admin-status-text">{user.is_enabled ? 'Enabled' : 'Disabled'}</span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="btn btn-sm btn-deactivate"
                        onClick={() => handleDelete(user.id)}
                        disabled={!user.is_enabled}
                        title="Disable user's access"
                      >
                        Disable
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SuperAdmin;
